// layoutEntityFan — the PURE placement half of "Expand" (spec §3.2).
//
// Given the focus node's rect and the neighbours the relations endpoint
// returned, decide where each new entity node goes. No state reads, no DOM, no
// clock, no randomness: the same input always produces the same output, which
// is what makes an expand idempotent (expand → collapse → expand puts the same
// node back in the same place) and what makes it unit-testable.
//
// ── DIRECTION BY RELATION ─────────────────────────────────────────────────
// The compass is semantic, not decorative — it is how a reader scans a thread:
//
//   south  reply_to                              a conversation reads downward
//   east   attached_to                           what hangs off this thing
//   north  governs · cites                       the law-like thing ABOVE it
//   west   references · amends · supersedes      what it points back at
//          · party_to
//   ring   everything else (relates_to,          spread into the four
//          derived_from, variation_of,           diagonal columns
//          because_of, unknown relations)
//
// ── WHY THE BANDS CANNOT OVERLAP ──────────────────────────────────────────
// Each bucket owns an axis-aligned band that no other bucket can enter:
//
//   • north / south stack in ONE column centred on the focus, so their x band
//     is [cx - W/2, cx + W/2].
//   • east starts at max(focus.right, cx + W/2) + GAP — strictly right of that
//     band, even when the focus is NARROWER than a node (the case a naive
//     `focus.right + GAP` gets wrong, because a centred column overhangs a
//     narrow focus on both sides).
//   • west mirrors it.
//   • the ring uses a SECOND east/west column (one more W + GAP outward), so a
//     long east column and a long ring can never meet — the bug you get from
//     putting the diagonals in the first column and letting both grow.
//
// Within a column, consecutive slots are H + GAP_Y apart, so they cannot
// overlap each other either. `__tests__/entityLayout.test.js` asserts both
// properties over a generated fan rather than trusting this comment.

// Default entity-node geometry, in CANVAS space (the same coordinate space
// `canvas.items[key] = { x, y, w, h }` already stores page/component tiles in
// — plain numbers, not CSS, so design tokens do not apply here).
export const ENTITY_ITEM_W = 280
export const ENTITY_ITEM_H = 160
const GAP_X = 120
const GAP_Y = 64

// Relation → compass bucket. A relation absent from this map lands in 'ring'.
export const ENTITY_RELATION_DIRECTION = {
  reply_to: 'south',
  attached_to: 'east',
  governs: 'north',
  cites: 'north',
  references: 'west',
  amends: 'west',
  supersedes: 'west',
  party_to: 'west'
}

// The four ring slots, cycled in this order so the first four "other"
// neighbours land in four different quadrants instead of stacking in one.
const RING_ORDER = ['ne', 'sw', 'nw', 'se']

// Normalise the `taken` argument — a Set, an array of keys, or an items MAP
// (`canvas.items`) all mean the same thing here: keys already on the board.
const _takenSet = (taken) => {
  if (!taken) return new Set()
  if (taken instanceof Set) return taken
  if (Array.isArray(taken)) return new Set(taken)
  if (typeof taken === 'object') return new Set(Object.keys(taken))
  return new Set()
}

// The item key for a neighbour. Accepts the string form directly and the
// EntityRef object form; anything else has no key and is skipped.
const _keyOf = (ref) => {
  if (!ref) return ''
  if (typeof ref === 'string') return ref.indexOf('ref:') === 0 ? ref : ''
  const id = ref.id != null ? String(ref.id) : ''
  return ref.type && id ? 'ref:' + ref.type + ':' + id : ''
}

/**
 * layoutEntityFan(focusRect, neighbors, opts?)
 *
 * @param focusRect  { x, y, w, h } of the node being expanded, canvas space.
 * @param neighbors  [{ ref, relation, direction }] — the relations-endpoint
 *                   payload verbatim. `ref` may be the string or object form.
 *                   `direction` ('out' | 'in') is carried through untouched;
 *                   it decides the ARROW direction, never the placement.
 * @param opts       { taken } keys already on the board (Set | array | items
 *                   map) — those neighbours are skipped, so re-expanding a
 *                   node never duplicates or re-places an existing tile.
 *                   { w, h } override the node size.
 * @returns [{ key, ref, relation, direction, x, y, w, h }] in a stable order:
 *          north, east, south, west, ring — input order preserved inside each.
 */
export const layoutEntityFan = (focusRect, neighbors, opts) => {
  const list = Array.isArray(neighbors) ? neighbors : []
  if (!list.length) return []
  const focus = focusRect || {}
  const fx = Number(focus.x) || 0
  const fy = Number(focus.y) || 0
  const fw = Number(focus.w) || ENTITY_ITEM_W
  const fh = Number(focus.h) || ENTITY_ITEM_H
  const W = (opts && Number(opts.w)) || ENTITY_ITEM_W
  const H = (opts && Number(opts.h)) || ENTITY_ITEM_H
  const taken = _takenSet(opts && opts.taken)

  const cx = fx + fw / 2
  const cy = fy + fh / 2
  const colX = cx - W / 2
  // Push east/west clear of BOTH the focus box and the centred north/south
  // column — see the band proof in the header.
  const eastX = Math.max(fx + fw, colX + W) + GAP_X
  const westX = Math.min(fx, colX) - GAP_X - W
  const ringEastX = eastX + W + GAP_X
  const ringWestX = westX - W - GAP_X

  // Bucket first, place second. Bucketing in a separate pass is what keeps the
  // per-bucket indices (and therefore the coordinates) independent of how the
  // endpoint happened to interleave relations in its response.
  const buckets = { north: [], east: [], south: [], west: [], ring: [] }
  const seen = new Set()
  for (const n of list) {
    if (!n) continue
    const key = _keyOf(n.ref)
    if (!key || taken.has(key) || seen.has(key)) continue
    seen.add(key)
    const relation = n.relation || 'relates_to'
    const dir = ENTITY_RELATION_DIRECTION[relation] || 'ring'
    buckets[dir].push({
      key,
      ref: n.ref,
      relation,
      direction: n.direction === 'in' ? 'in' : 'out'
    })
  }

  const northY = (i) => fy - (i + 1) * (H + GAP_Y)
  const southY = (i) => fy + fh + GAP_Y + i * (H + GAP_Y)
  const sideY = (i) => cy - H / 2 + i * (H + GAP_Y)

  const out = []
  buckets.north.forEach((n, i) => out.push({ ...n, x: colX, y: northY(i), w: W, h: H }))
  buckets.east.forEach((n, i) => out.push({ ...n, x: eastX, y: sideY(i), w: W, h: H }))
  buckets.south.forEach((n, i) => out.push({ ...n, x: colX, y: southY(i), w: W, h: H }))
  buckets.west.forEach((n, i) => out.push({ ...n, x: westX, y: sideY(i), w: W, h: H }))
  // Ring: cycle the four diagonal columns, stepping one row further out each
  // time a quadrant comes round again. `ring` never enters the first east/west
  // column, so a long east column and a long ring cannot collide.
  buckets.ring.forEach((n, i) => {
    const slot = RING_ORDER[i % RING_ORDER.length]
    const step = Math.floor(i / RING_ORDER.length)
    const x = slot === 'ne' || slot === 'se' ? ringEastX : ringWestX
    const y = slot === 'ne' || slot === 'nw' ? northY(step) : southY(step)
    out.push({ ...n, x, y, w: W, h: H })
  })
  return out
}
